import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Ear, 
  Users, 
  MessageCircle, 
  ShoppingCart, 
  Calendar, 
  BarChart3, 
  CheckCircle2, 
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Star,
  Shield,
  Zap,
  Heart,
  Eye,
  EyeOff,
  LogIn
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage({ user, setUser }) {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error("Please enter email and password");
      return;
    }
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: loginForm.email,
        password: loginForm.password
      }, { withCredentials: true });
      
      // Store session token in localStorage as backup
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      
      toast.success("Login successful!");
      setShowLoginModal(false);
      if (setUser) setUser(response.data);
      navigate('/dashboard');
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.phone) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/webhooks/lead-capture`, {
        ...contactForm,
        source: "website",
        page: "landing"
      });
      toast.success("Thank you! We'll contact you soon.");
      setContactForm({ name: "", phone: "", email: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  const features = [
    { icon: Users, title: "Lead Management", desc: "Track every prospect from first contact to conversion" },
    { icon: MessageCircle, title: "WhatsApp Integration", desc: "Engage customers on their preferred channel" },
    { icon: ShoppingCart, title: "E-commerce Ready", desc: "Seamless Shopify integration for online sales" },
    { icon: Calendar, title: "Virtual Clinic", desc: "Schedule and manage remote consultations" },
    { icon: BarChart3, title: "Analytics", desc: "Data-driven insights for growth" },
    { icon: Zap, title: "AI Powered", desc: "Smart product recommendations" }
  ];

  const stats = [
    { value: "50K+", label: "Happy Customers" },
    { value: "98%", label: "Satisfaction Rate" },
    { value: "15+", label: "Years Experience" },
    { value: "100+", label: "Clinic Partners" }
  ];

  if (user) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_heartech-dash/artifacts/7jmxr97o_hearclear%20logo.png" 
                alt="HearClear" 
                className="h-10 w-auto"
              />
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-cyan-700 transition-colors">Features</a>
              <a href="#products" className="text-slate-600 hover:text-cyan-700 transition-colors">Products</a>
              <a href="#contact" className="text-slate-600 hover:text-cyan-700 transition-colors">Contact</a>
            </nav>
            <Button 
              onClick={() => setShowLoginModal(true)}
              data-testid="login-btn"
              className="bg-cyan-700 hover:bg-cyan-800"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">
              <img 
                src="https://hearclear.in/cdn/shop/files/HearClear-logo.svg"
                alt="HearClear"
                className="h-10 mx-auto mb-2"
              />
              Sign in to HearClear CRM
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordLogin} className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-cyan-700 hover:bg-cyan-800"
              disabled={loggingIn}
              data-testid="login-submit-btn"
            >
              {loggingIn ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">or</span>
              </div>
            </div>
            <Button 
              type="button"
              variant="outline" 
              className="w-full"
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
            <p className="text-xs text-center text-slate-500 mt-4">
              Contact admin if you need an account
            </p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      <section className="pt-32 pb-20 gradient-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-teal-50 text-cyan-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4" />
                India's Trusted Hearing Care Partner
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight font-[Manrope] tracking-tight">
                Hear Life's <span className="text-cyan-700">Beautiful</span> Moments
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-lg">
                Advanced hearing solutions powered by AI, delivered with care. Experience the future of audiology with HearClear's unified platform.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  className="bg-orange-500 hover:bg-orange-600 text-white px-8"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="book-consultation-btn"
                >
                  Book Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-cyan-700 text-cyan-700 hover:bg-cyan-700 hover:text-white"
                  onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Products
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-6">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-slate-200 border-2 border-white"></div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600">Trusted by 50,000+ customers</p>
                </div>
              </div>
            </div>
            <div className="relative animate-fade-in">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-200/30 to-teal-200/30 rounded-3xl transform rotate-3"></div>
              <img 
                src="https://images.pexels.com/photos/7179255/pexels-photo-7179255.jpeg"
                alt="Doctor consulting patient"
                className="relative rounded-3xl shadow-2xl object-cover w-full h-[500px]"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl animate-slide-up stagger-2">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">98% Success Rate</p>
                    <p className="text-sm text-slate-500">In hearing improvement</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-cyan-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-white font-[Manrope]">{stat.value}</p>
                <p className="mt-2 text-cyan-100">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-[Manrope]">
              Complete Healthcare Platform
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Everything you need to manage leads, customers, and grow your hearing care business
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="card-hover border-0 shadow-md">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-cyan-700" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 font-[Manrope]">{feature.title}</h3>
                  <p className="mt-2 text-slate-600">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Products Preview */}
      <section id="products" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-[Manrope]">
              Premium Hearing Solutions
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Discover hearing aids that fit your lifestyle and budget
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: "Signia Motion X", price: "₹25,000", band: "Economy", img: "https://images.unsplash.com/photo-1596088869451-491e167efabb?crop=entropy&cs=srgb&fm=jpg&w=400&q=85" },
              { name: "Signia Stiletto IX", price: "₹85,000", band: "Premium", img: "https://images.unsplash.com/photo-1596088728260-08a654466a00?crop=entropy&cs=srgb&fm=jpg&w=400&q=85", featured: true },
              { name: "Signia Styletto AX", price: "₹95,000", band: "Premium+", img: "https://images.pexels.com/photos/14682242/pexels-photo-14682242.jpeg?w=400" }
            ].map((product, index) => (
              <Card key={index} className={`overflow-hidden card-hover ${product.featured ? 'ring-2 ring-orange-500' : ''}`}>
                {product.featured && (
                  <div className="bg-orange-500 text-white text-center py-2 text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <div className="aspect-square bg-slate-100 relative overflow-hidden">
                  <img src={product.img} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <CardContent className="p-6">
                  <span className="text-sm font-medium text-cyan-700">{product.band}</span>
                  <h3 className="text-xl font-semibold text-slate-900 mt-1 font-[Manrope]">{product.name}</h3>
                  <p className="text-2xl font-bold text-slate-900 mt-2">{product.price}</p>
                  <Button className="w-full mt-4 bg-cyan-700 hover:bg-cyan-800">Learn More</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-6">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <Shield className="w-8 h-8 text-cyan-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 font-[Manrope]">3-Year Warranty</h3>
              <p className="mt-2 text-slate-600">Extended protection on all premium devices</p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <Heart className="w-8 h-8 text-cyan-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 font-[Manrope]">Free Trial Period</h3>
              <p className="mt-2 text-slate-600">30-day money-back guarantee</p>
            </div>
            <div className="p-6">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
                <Phone className="w-8 h-8 text-cyan-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 font-[Manrope]">24/7 Support</h3>
              <p className="mt-2 text-slate-600">Expert assistance whenever you need</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-[Manrope]">
                Book Your Free Consultation
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Our hearing care experts are ready to help you find the perfect solution. Fill in your details and we'll reach out within 24 hours.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                    <Phone className="w-5 h-5 text-cyan-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Call Us</p>
                    <p className="text-slate-600">+91 95055 40400</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-cyan-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Email Us</p>
                    <p className="text-slate-600">care@hearclear.in</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-cyan-700" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Visit Us</p>
                    <p className="text-slate-600">Pan India - 100+ Clinic Partners</p>
                  </div>
                </div>
              </div>
            </div>
            <Card className="shadow-xl border-0">
              <CardContent className="p-8">
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div>
                    <label className="form-label">Full Name *</label>
                    <Input 
                      placeholder="Enter your name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="form-input"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Phone Number *</label>
                    <Input 
                      placeholder="Enter your phone number"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      className="form-input"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">Email (Optional)</label>
                    <Input 
                      type="email"
                      placeholder="Enter your email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="form-input"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? "Submitting..." : "Request Callback"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-cyan-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white font-[Manrope]">
            Ready to Transform Your Hearing Care Business?
          </h2>
          <p className="mt-4 text-xl text-cyan-100">
            Join HearClear's platform and grow your practice with AI-powered tools
          </p>
          <Button 
            size="lg" 
            className="mt-8 bg-white text-cyan-700 hover:bg-slate-100 px-8"
            onClick={handleGoogleLogin}
            data-testid="cta-signin-btn"
          >
            Get Started Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_heartech-dash/artifacts/7jmxr97o_hearclear%20logo.png" 
                alt="HearClear" 
                className="h-10 w-auto brightness-0 invert"
              />
            </div>
            <p className="mt-4 md:mt-0 text-slate-400 text-sm">
              © 2026 HearClear India. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
